import React, { useEffect, useState, useRef } from 'react';
import './RouteInfoWindow.css';

const RouteInfoWindow = ({ routeInfo, position, onClose, onTravelModeChange, onSegmentSelect, selectedSegmentIndex, mapRef }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dialogPosition, setDialogPosition] = useState({ x: 50, y: 50 }); // Use absolute position
  const [isMobile, setIsMobile] = useState(false);
  const dialogRef = useRef(null);

  // Mobile detection
  useEffect(() => {
    const mediaQuery = window.matchMedia('(hover: none) and (pointer: coarse)');
    setIsMobile(mediaQuery.matches);
    
    const handleChange = (e) => setIsMobile(e.matches);
    mediaQuery.addListener(handleChange);
    
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  // Set initial position when routeInfo changes
  useEffect(() => {
    if (routeInfo && mapRef && mapRef.current) {
      try {
        const map = mapRef.current;
        const mapDiv = map.getDiv();
        const mapBounds = mapDiv.getBoundingClientRect();
        
        // Set initial position relative to the map
        setDialogPosition({ 
          x: mapBounds.left + Math.max(20, Math.min(300, mapBounds.width - 370)),
          y: mapBounds.top + Math.max(20, Math.min(100, mapBounds.height - 420))
        });
      } catch (error) {
        console.error('Error calculating initial position:', error);
        // Fallback position
        setDialogPosition({ x: 50, y: 50 });
      }
    }
  }, [routeInfo, mapRef]);

  // Add ESC key listener
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [onClose]);

  // Drag functionality
  const handleMouseDown = (e) => {
    try {
      if (e.target.closest('.myMap-route-segments') || e.target.closest('select')) {
        return; // Don't start dragging if clicking on scrollable content or dropdown
      }
      
      if (!dialogRef.current) return;
      
      setIsDragging(true);
      const rect = dialogRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      e.preventDefault();
    } catch (error) {
      console.error('Error in handleMouseDown:', error);
    }
  };

  // Add global mouse event listeners for dragging
  useEffect(() => {
    const handleMouseMoveLocal = (e) => {
      try {
        if (!isDragging) return;
        
        const newPosition = {
          x: e.clientX - dragOffset.x,
          y: e.clientY - dragOffset.y
        };
        
        // Keep dialog within viewport bounds
        const maxX = window.innerWidth - 350; // Dialog width
        const maxY = window.innerHeight - 400; // Dialog height
        
        newPosition.x = Math.max(20, Math.min(newPosition.x, maxX));
        newPosition.y = Math.max(20, Math.min(newPosition.y, maxY));
        
        setDialogPosition(newPosition);
      } catch (error) {
        console.error('Error in handleMouseMove:', error);
      }
    };

    const handleMouseUpLocal = () => {
      try {
        setIsDragging(false);
      } catch (error) {
        console.error('Error in handleMouseUp:', error);
      }
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMoveLocal);
      document.addEventListener('mouseup', handleMouseUpLocal);
      document.body.style.userSelect = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMoveLocal);
        document.removeEventListener('mouseup', handleMouseUpLocal);
        document.body.style.userSelect = '';
      };
    }
  }, [isDragging, dragOffset]);

  const handleTravelModeChange = (event) => {
    try {
      const newTravelMode = event.target.value;
      if (onTravelModeChange) {
        onTravelModeChange(newTravelMode);
      }
    } catch (error) {
      console.error('Error in handleTravelModeChange:', error);
    }
  };

  const handleSegmentClick = (segmentIndex) => {
    try {
      if (onSegmentSelect) {
        // If clicking the same segment, deselect it, otherwise select the new one
        const newSelectedIndex = selectedSegmentIndex === segmentIndex ? -1 : segmentIndex;
        onSegmentSelect(newSelectedIndex);
      }
    } catch (error) {
      console.error('Error in handleSegmentClick:', error);
    }
  };

  if (!routeInfo || !position) return null;

  // Safety check for routeInfo properties
  if (!routeInfo.startAddress || !routeInfo.endAddress) {
    console.warn('Missing required routeInfo properties');
    return null;
  }

  return (
    <div 
      ref={dialogRef}
      className={`myMap-route-infowindow ${isDragging ? 'dragging' : ''}`}
      onMouseDown={handleMouseDown}
      style={{
        position: 'fixed', // Changed to fixed for absolute viewport positioning
        left: dialogPosition.x,
        top: dialogPosition.y,
        cursor: isDragging ? 'grabbing' : 'grab',
        zIndex: 10000,
        pointerEvents: 'auto'
      }}
    >
        <button 
          className="myMap-route-close-btn"
          onClick={onClose}
          title="Close (ESC)"
        >
          ×
        </button>
        
        <div className="myMap-route-title">
          {routeInfo.startAddress} → {routeInfo.endAddress}
        </div>
        
        <div className="myMap-route-info-row">
          <span className="myMap-route-label">Travel Mode:</span>
          <div className="myMap-travel-mode-controls">
            <select 
              className="myMap-travel-mode-select"
              value={routeInfo.travelMode}
              onChange={handleTravelModeChange}
            >
              <option value="DRIVING">🚗 Driving</option>
              <option value="WALKING">🚶 Walking</option>
              <option value="BICYCLING">🚴 Bicycling</option>
              <option value="TRANSIT">🚌 Transit</option>
            </select>
            <button 
              className="myMap-get-directions-btn"
              onClick={() => {
                try {
                  // Create Google Maps URL with directions
                  const origin = encodeURIComponent(routeInfo.startAddress);
                  const destination = encodeURIComponent(routeInfo.endAddress);
                  
                  // Add waypoints if there are multiple legs
                  let waypoints = '';
                  if (routeInfo.legs && routeInfo.legs.length > 1) {
                    const waypointAddresses = routeInfo.legs.slice(0, -1).map(leg => 
                      encodeURIComponent(leg.end_address)
                    );
                    waypoints = `&waypoints=${waypointAddresses.join('|')}`;
                  }
                  
                  // Convert travel mode to Google Maps format
                  let travelMode = 'driving';
                  switch (routeInfo.travelMode) {
                    case 'WALKING':
                      travelMode = 'walking';
                      break;
                    case 'BICYCLING':
                      travelMode = 'bicycling';
                      break;
                    case 'TRANSIT':
                      travelMode = 'transit';
                      break;
                    default:
                      travelMode = 'driving';
                  }
                  
                  const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}${waypoints}&travelmode=${travelMode}`;
                  
                  // Open in new tab
                  window.open(googleMapsUrl, '_blank', 'noopener,noreferrer');
                } catch (error) {
                  console.error('Error opening Google Maps directions:', error);
                  alert('Could not open directions. Please try again.');
                }
              }}
              title="Open route in Google Maps for navigation"
            >
              🗺️
            </button>
          </div>
        </div>
        
        <div className="myMap-route-info-row">
          <span className="myMap-route-label">Total Distance / Time:</span>
          <span className="myMap-route-value">{routeInfo.distanceText} / {routeInfo.durationText}</span>
        </div>
        
        {/* Route segments breakdown - hidden on mobile */}
        {!isMobile && (
          <div className="myMap-route-segments">
            <div className="myMap-route-segments-title">Route Breakdown:</div>
            {routeInfo.legs && Array.isArray(routeInfo.legs) && routeInfo.legs.map((leg, index) => {
              // Safety check for leg properties
              if (!leg || !leg.start_address || !leg.end_address || !leg.distance || !leg.duration) {
                console.warn('Invalid leg data at index:', index, leg);
                return null;
              }
              
              return (
                <div 
                  key={index} 
                  className={`myMap-route-segment ${selectedSegmentIndex === index ? 'selected' : ''}`}
                  onClick={() => handleSegmentClick(index)}
                >
                  <div className="myMap-route-segment-locations">
                    <span className="myMap-route-segment-from">{leg.start_address}</span>
                    <span className="myMap-route-segment-arrow">→</span>
                    <span className="myMap-route-segment-to">{leg.end_address}</span>
                  </div>
                  <div className="myMap-route-segment-details">
                    <span className="myMap-route-segment-distance">{leg.distance?.text || 'N/A'}</span>
                    <span className="myMap-route-segment-duration">{leg.duration?.text || 'N/A'}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
  );
};

export default RouteInfoWindow;
